import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Avatar,
  Stack,
  Badge,
  TextField
} from '@mui/material';
import {
  Insights,
  TrendingUp,
  TrendingDown,
  Remove,
  Visibility,
  Phone,
  Language,
  LocationOn,
  CalendarToday,
  Refresh,
  Download,
  ArrowUpward,
  ArrowDownward,
  ShowChart
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ReferenceLine
} from 'recharts';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useBusiness } from '../../contexts/BusinessContext';
import { useBusinessSearch } from '../../contexts/BusinessSearchContext';

const InsightsPage = () => {
  const { locationId: routeLocationId } = useParams();
  const { selectedBusiness } = useBusiness();
  const { minimalBusinesses } = useBusinessSearch();
  
  // Use selected business from context, or fall back to URL parameter
  const locationId = selectedBusiness?.id || selectedBusiness?.locationId || selectedBusiness?.googleLocationId || routeLocationId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('last_week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customStartMonth, setCustomStartMonth] = useState('');
  const [customEndMonth, setCustomEndMonth] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [error, setError] = useState(null);

  // Fixed three core KPIs
  const coreKPIs = [
    {
      key: 'BUSINESS_IMPRESSIONS',
      label: 'Einbledungen',
      icon: <Visibility />,
      color: '#2196f3',
      description: 'Gesamte Sichtbarkeit in Google (Search + Maps)',
      metrics: ['BUSINESS_IMPRESSIONS_DESKTOP_SEARCH', 'BUSINESS_IMPRESSIONS_MOBILE_SEARCH', 'BUSINESS_IMPRESSIONS_DESKTOP_MAPS', 'BUSINESS_IMPRESSIONS_MOBILE_MAPS']
    },
    {
      key: 'CALL_CLICKS',
      label: 'Anrufe',
      icon: <Phone />,
      color: '#4caf50',
      description: 'Anzahl der Anrufe über Google',
      metrics: ['CALL_CLICKS']
    },
    {
      key: 'WEBSITE_CLICKS',
      label: 'Webseiten-Besucher',
      icon: <Language />,
      color: '#ff9800',
      description: 'Anzahl der Website-Besuche über Google',
      metrics: ['WEBSITE_CLICKS']
    }
  ];

  // Comprehensive time period options
  const periodOptions = [
    { 
      value: 'today', 
      label: 'Heute',
      getDates: () => {
        const today = new Date();
        return { start: today, end: today };
      }
    },
    { 
      value: 'yesterday', 
      label: 'Gestern',
      getDates: () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: yesterday };
      }
    },
    { 
      value: 'last_week', 
      label: 'Letzte 7 Tage',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 7);
        return { start, end };
      }
    },
    { 
      value: 'last_30_days', 
      label: 'Letzte 30 Tage',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        return { start, end };
      }
    },
    { 
      value: 'last_month', 
      label: 'Letzter Monat',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        return { start, end };
      }
    },
    { 
      value: 'last_3_months', 
      label: 'Letzte 3 Monate',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 3);
        return { start, end };
      }
    },
    { 
      value: 'last_6_months', 
      label: 'Letzte 6 Monate',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 6);
        return { start, end };
      }
    },
    {
      value: 'last_year',
      label: 'Letzte 12 Monate',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setFullYear(start.getFullYear() - 1);
        return { start, end };
      }
    },
    {
      value: 'custom',
      label: 'Benutzerdefiniert',
      getDates: () => ({
        start: customStartDate ? new Date(customStartDate) : new Date(),
        end: customEndDate ? new Date(customEndDate) : new Date()
      })
    },
    {
      value: 'custom_month',
      label: 'Monatsbereich',
      getDates: () => {
        // 'YYYY-MM' -> first day of start month to last day of end month (capped at today)
        const start = customStartMonth ? new Date(`${customStartMonth}-01`) : new Date();
        let end = new Date();
        if (customEndMonth) {
          const [y, m] = customEndMonth.split('-').map(Number);
          end = new Date(y, m, 0); // day 0 of next month = last day of end month
          const today = new Date();
          if (end > today) end = today;
        }
        return { start, end };
      }
    }
  ];

  // Human-readable resolved date range for the currently selected period
  const getActiveRangeLabel = () => {
    if (selectedPeriod === 'custom' && (!customStartDate || !customEndDate)) return null;
    if (selectedPeriod === 'custom_month' && (!customStartMonth || !customEndMonth)) return null;
    const cfg = periodOptions.find(p => p.value === selectedPeriod);
    if (!cfg) return null;
    const { start, end } = cfg.getDates();
    const fmt = (d) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
  };
  const activeRangeLabel = getActiveRangeLabel();

  // Load business info and insights data when locationId changes
  useEffect(() => {
    console.log('🔄 Location ID changed:', locationId);
    if (locationId && locationId !== 'undefined') {
      loadBusinessInfo();
      loadInsightsData();
    }
  }, [locationId]);

  // React to selected business changes from dropdown
  useEffect(() => {
    if (selectedBusiness) {
      console.log('🔄 Selected business changed:', selectedBusiness.businessName);
      const newLocationId = selectedBusiness.id || selectedBusiness.locationId || selectedBusiness.googleLocationId;
      if (newLocationId && newLocationId !== locationId) {
        console.log('📍 Updating insights for new business:', newLocationId);
        // The locationId will update and trigger the first useEffect
      }
    }
  }, [selectedBusiness]);

  // Reload data when period changes
  useEffect(() => {
    if (locationId && locationId !== 'undefined') {
      // For a custom range, wait until both dates/months are picked
      if (selectedPeriod === 'custom' && (!customStartDate || !customEndDate)) return;
      if (selectedPeriod === 'custom_month' && (!customStartMonth || !customEndMonth)) return;
      console.log('📅 Period changed, reloading data:', selectedPeriod);
      loadInsightsData();
    }
  }, [selectedPeriod, customStartDate, customEndDate, customStartMonth, customEndMonth]);

  const loadBusinessInfo = async () => {
    try {
      const response = await axios.get(`/api/businesses/${locationId}/ranking-overview`);

      console.log('---bisines info api',response.data)
      if (response.data.success) {
        setBusinessInfo({
          businessName: response.data.data.businessName,
          location: response.data.location,
          googleLocationId: locationId
        });
      }
    } catch (error) {
      console.error('Error loading business info:', error);
    }
  };

  const loadInsightsData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentifizierung erforderlich. Bitte melden Sie sich an.');
        setLoading(false);
        return;
      }

      // Check localStorage cache (4 hours TTL) if not forcing refresh
      let periodKey = selectedPeriod;
      if (selectedPeriod === 'custom') periodKey = `custom_${customStartDate}_${customEndDate}`;
      if (selectedPeriod === 'custom_month') periodKey = `custommonth_${customStartMonth}_${customEndMonth}`;
      const cacheKey = `insightsData_${locationId}_${periodKey}`;
      if (!forceRefresh) {
        const cached = localStorage.getItem(cacheKey);
        const cachedTime = localStorage.getItem(`${cacheKey}_time`);
        const FOUR_HOURS = 4 * 60 * 60 * 1000;

        if (cached && cachedTime && Date.now() - parseInt(cachedTime) < FOUR_HOURS) {
          setInsightsData(JSON.parse(cached));
          setLoading(false);
          return;
        }
      } else {
        // Clear cache when forcing refresh
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
      }

      // Custom range needs both dates/months picked before loading anything
      if (selectedPeriod === 'custom' && (!customStartDate || !customEndDate)) {
        setLoading(false);
        return;
      }
      if (selectedPeriod === 'custom_month' && (!customStartMonth || !customEndMonth)) {
        setLoading(false);
        return;
      }

      // Get date range based on selected period
      const periodConfig = periodOptions.find(p => p.value === selectedPeriod);
      const { start: startDate, end: endDate } = periodConfig.getDates();

      // Get all metrics needed for the three KPIs
      const allMetrics = coreKPIs.reduce((acc, kpi) => [...acc, ...kpi.metrics], []);

      console.log('📊 Loading insights data:', {
        locationId,
        period: selectedPeriod,
        dateRange: `${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`,
        metrics: allMetrics
      });

      const Start_Date={
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
            day: startDate.getDate()
          }


        const End_Date=  {
            year: endDate.getFullYear(),
            month: endDate.getMonth() + 1,
            day: endDate.getDate()
          }



      const response = await axios.post(`/api/businesses/${locationId}/insights`, {
        metrics: allMetrics,
        dateRange: {
          startDate:Start_Date ,
          endDate: End_Date
        }
      });

      if (response.data.success) {
        setInsightsData(response.data.data);
        // Cache in localStorage for 4 hours
        localStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
        console.log('------insigts',response.data.data)
        console.log('✅ Insights data loaded successfully');
      } else {
        setError(response.data.error || 'Failed to load insights data');
      }

    } catch (error) {
      console.error('Error loading insights:', error);
      
      if (error.response?.status === 401) {
        // Don't redirect for API 401 errors - just show the error
        setError('Google API-Authentifizierung fehlgeschlagen. Demo-Daten werden angezeigt.');
        console.log('ℹ️ Using fallback for 401 Google API error');
      } else if (error.response?.status === 403) {
        setError('Keine Berechtigung für Insights-Daten. Kontaktieren Sie Ihren Administrator.');
      } else if (error.response?.status === 404) {
        setError('Business-Profil nicht gefunden.');
      } else {
        setError(error.response?.data?.error || error.message || 'Fehler beim Laden der Insights-Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  // Process data for the three core KPIs
  const processKPIData = () => {
    if (!insightsData || !insightsData.timeSeries) {
      console.log('⚠️ No insights data or timeSeries available');
      return [];
    }

    console.log('📊 Processing KPI data:', {
      timeSeriesKeys: Object.keys(insightsData.timeSeries),
      sampleMetric: Object.keys(insightsData.timeSeries)[0],
      sampleData: insightsData.timeSeries[Object.keys(insightsData.timeSeries)[0]]?.slice(0, 2)
    });

    const dateMap = new Map();

    // Process time series data and aggregate for KPIs
    Object.keys(insightsData.timeSeries).forEach(metric => {
      const metricData = insightsData.timeSeries[metric] || [];
      console.log(`📈 Processing metric ${metric}: ${metricData.length} data points`);
      
      metricData.forEach((dataPoint, index) => {
        const dateKey = `${dataPoint.date.year}-${String(dataPoint.date.month).padStart(2, '0')}-${String(dataPoint.date.day).padStart(2, '0')}`;
        
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            date: dateKey,
            formattedDate: new Date(dataPoint.date.year, dataPoint.date.month - 1, dataPoint.date.day).toLocaleDateString('de-DE'),
            BUSINESS_IMPRESSIONS: 0,
            CALL_CLICKS: 0,
            WEBSITE_CLICKS: 0
          });
        }
        
        const entry = dateMap.get(dateKey);
        const value = dataPoint.value || 0;
        
        // Log each data point for debugging
        if (index < 3) { // Log first 3 entries for each metric
          console.log(`   ${dateKey}: ${metric} = ${value}`);
        }
        
        // Aggregate business impressions from all sources
        if (metric.includes('BUSINESS_IMPRESSIONS')) {
          entry.BUSINESS_IMPRESSIONS += value;
        } else if (metric === 'CALL_CLICKS') {
          entry.CALL_CLICKS += value; // Use += in case there are multiple call click metrics
        } else if (metric === 'WEBSITE_CLICKS') {
          entry.WEBSITE_CLICKS += value; // Use += in case there are multiple website click metrics
        }
      });
    });

    const processedData = Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log('📊 Processed KPI data:', {
      totalDays: processedData.length,
      sampleDay: processedData[0],
      totalImpressions: processedData.reduce((sum, day) => sum + day.BUSINESS_IMPRESSIONS, 0),
      totalCalls: processedData.reduce((sum, day) => sum + day.CALL_CLICKS, 0),
      totalWebsite: processedData.reduce((sum, day) => sum + day.WEBSITE_CLICKS, 0)
    });

    return processedData;
  };

  // Calculate KPI totals and before/after figures
  const calculateKPITotals = () => {
    if (!insightsData || !insightsData.timeSeries) {
      console.log('⚠️ No data for KPI totals calculation');
      return {};
    }

    const kpiTotals = {};
    const kpiBeforeAfter = {};
    
    // Get business creation date as reference point
    const businessCreatedAt = insightsData?.businessInfo?.createdAt;
    const referenceDate = businessCreatedAt ? new Date(businessCreatedAt) : null;
    
    console.log('🔢 Calculating KPI totals and before/after figures...');
    console.log('📅 Reference date (business creation):', referenceDate?.toLocaleDateString('de-DE'));
    
    coreKPIs.forEach(kpi => {
      let total = 0;
      let beforeTotal = 0;
      let afterTotal = 0;
      let beforeDays = 0;
      let afterDays = 0;
      
      console.log(`📊 Calculating total for ${kpi.key}:`);
      
      kpi.metrics.forEach(metric => {
        const metricData = insightsData.timeSeries[metric] || [];
        
        metricData.forEach(dataPoint => {
          const pointDate = new Date(dataPoint.date.year, dataPoint.date.month - 1, dataPoint.date.day);
          const value = dataPoint.value || 0;
          total += value;
          
          // Split into before/after reference date
          if (referenceDate) {
            if (pointDate < referenceDate) {
              beforeTotal += value;
              if (value > 0) beforeDays++;
            } else {
              afterTotal += value;  
              if (value > 0) afterDays++;
            }
          }
        });
        
        const metricTotal = metricData.reduce((sum, dataPoint) => sum + (dataPoint.value || 0), 0);
        console.log(`   ${metric}: ${metricTotal} (from ${metricData.length} data points)`);
      });
      
      kpiTotals[kpi.key] = total;
      
      // Calculate averages per day for before/after periods
      kpiBeforeAfter[kpi.key] = {
        beforeTotal,
        afterTotal,
        beforeAvgPerDay: beforeDays > 0 ? (beforeTotal / beforeDays) : 0,
        afterAvgPerDay: afterDays > 0 ? (afterTotal / afterDays) : 0,
        beforeDays,
        afterDays
      };
      
      console.log(`✅ ${kpi.key} - Total: ${total}, Before: ${beforeTotal}/${beforeDays} days (${kpiBeforeAfter[kpi.key].beforeAvgPerDay.toFixed(1)}/day), After: ${afterTotal}/${afterDays} days (${kpiBeforeAfter[kpi.key].afterAvgPerDay.toFixed(1)}/day)`);
    });

    console.log('📊 Final KPI totals:', kpiTotals);
    return { totals: kpiTotals, beforeAfter: kpiBeforeAfter };
  };

  // Calculate trend for KPIs
  const calculateTrend = (kpiKey, chartData) => {
    if (chartData.length < 2) return null;

    const recentData = chartData.slice(-3); // Last 3 days
    const olderData = chartData.slice(-6, -3); // Previous 3 days

    if (recentData.length === 0 || olderData.length === 0) return null;

    const recentAvg = recentData.reduce((sum, day) => sum + (day[kpiKey] || 0), 0) / recentData.length;
    const olderAvg = olderData.reduce((sum, day) => sum + (day[kpiKey] || 0), 0) / olderData.length;

    if (olderAvg === 0) return null;

    return ((recentAvg - olderAvg) / olderAvg) * 100;
  };

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Lade Insights-Daten{selectedBusiness?.businessName ? ` für ${selectedBusiness.businessName}` : ''}...
        </Typography>
      </Box>
    );
  }

  // Show message if no business selected
  if (!locationId || locationId === 'undefined') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <Insights sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" color="textSecondary" gutterBottom>
            Kein Business ausgewählt
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Bitte wählen Sie ein Business aus der Dropdown-Liste aus, um Insights-Daten anzuzeigen.
          </Typography>
        </Box>
      </Box>
    );
  }

  const chartData = processKPIData();
  const kpiData = calculateKPITotals();
  const kpiTotals = kpiData.totals || {};
  const kpiBeforeAfter = kpiData.beforeAfter || {};
  
  // Get business creation date for reference line
  const businessCreatedAt = insightsData?.businessInfo?.createdAt;
  const creationDateForChart = businessCreatedAt ? new Date(businessCreatedAt).toLocaleDateString('de-DE') : null;
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Geschäftseinblicke
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {selectedBusiness?.businessName || businessInfo?.businessName || 'Business'} • Google My Business Performance
          </Typography>
        </Box>
        
        <Box display="flex" gap={2}>
          <Tooltip title="Daten aktualisieren">
            <IconButton onClick={() => loadInsightsData(true)} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Daten exportieren">
            <IconButton disabled={!insightsData}>
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Time Period Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                📅 Zeitraum auswählen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeRangeLabel
                  ? `Angezeigter Zeitraum: ${activeRangeLabel}`
                  : 'Bitte Start und Ende auswählen'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Zeitraum</InputLabel>
                <Select
                  value={selectedPeriod}
                  label="Zeitraum"
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                >
                  {periodOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedPeriod === 'custom' && (
                <>
                  <TextField
                    label="Von"
                    type="date"
                    size="small"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: customEndDate || new Date().toISOString().split('T')[0] }}
                    sx={{ '& input': { textTransform: 'uppercase' } }}
                  />
                  <TextField
                    label="Bis"
                    type="date"
                    size="small"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: customStartDate || undefined, max: new Date().toISOString().split('T')[0] }}
                    sx={{ '& input': { textTransform: 'uppercase' } }}
                  />
                </>
              )}
              {selectedPeriod === 'custom_month' && (
                <>
                  <TextField
                    label="Von Monat"
                    type="month"
                    size="small"
                    value={customStartMonth}
                    onChange={(e) => setCustomStartMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: customEndMonth || new Date().toISOString().slice(0, 7) }}
                    sx={{ minWidth: 200, '& input': { textTransform: 'uppercase' } }}
                  />
                  <TextField
                    label="Bis Monat"
                    type="month"
                    size="small"
                    value={customEndMonth}
                    onChange={(e) => setCustomEndMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: customStartMonth || undefined, max: new Date().toISOString().slice(0, 7) }}
                    sx={{ minWidth: 200, '& input': { textTransform: 'uppercase' } }}
                  />
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Debug Data Display 
      {insightsData && process.env.NODE_ENV === 'development' && (
        <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔍 Debug: API Response Data
            </Typography>
            <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
              {JSON.stringify(insightsData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      */}

      {insightsData && (
        <>
          {/* Enhanced KPI Dashboard */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {coreKPIs.map((kpi, index) => {
              const total = kpiTotals[kpi.key] || 0;
              const average = chartData.length > 0 ? (total / chartData.length).toFixed(1) : 0;
              const trend = calculateTrend(kpi.key, chartData);
              const beforeAfterData = kpiBeforeAfter[kpi.key];

              // Calculate performance improvement percentage
              let improvementPercentage = 0;
              let hasImprovement = false;
              if (beforeAfterData && beforeAfterData.beforeAvgPerDay > 0 && beforeAfterData.afterAvgPerDay > 0) {
                improvementPercentage = ((beforeAfterData.afterAvgPerDay - beforeAfterData.beforeAvgPerDay) / beforeAfterData.beforeAvgPerDay * 100);
                hasImprovement = Math.abs(improvementPercentage) > 0.1;
              }

              return (
                <Grid item xs={12} md={4} key={kpi.key}>
                  <Card 
                    elevation={3}
                    sx={{ 
                      height: '100%',
                      background: `linear-gradient(135deg, ${kpi.color}08 0%, ${kpi.color}15 100%)`,
                      border: `2px solid ${kpi.color}20`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 40px ${kpi.color}25`,
                        borderColor: `${kpi.color}40`
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Header with Icon and Title */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <Avatar 
                              sx={{ 
                                bgcolor: kpi.color,
                                width: 40,
                                height: 40,
                                fontSize: '1.2rem'
                              }}
                            >
                              {kpi.icon}
                            </Avatar>
                            <Box>
                              <Typography 
                                variant="h6" 
                                fontWeight="bold" 
                                color="text.primary"
                                sx={{ lineHeight: 1.2 }}
                              >
                                {kpi.label}
                              </Typography>
                              {hasImprovement && (
                                <Chip
                                  icon={improvementPercentage > 0 ? <ArrowUpward /> : <ArrowDownward />}
                                  label={`${improvementPercentage > 0 ? '+' : ''}${improvementPercentage.toFixed(1)}%`}
                                  size="small"
                                  color={improvementPercentage > 0 ? "success" : "error"}
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold'
                                  }}
                                />
                              )}
                            </Box>
                          </Stack>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: '0.85rem', lineHeight: 1.3 }}
                          >
                            {kpi.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Main Metric Display */}
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography 
                          variant="h2" 
                          component="div"
                          fontWeight="bold"
                          sx={{ 
                            background: `linear-gradient(45deg, ${kpi.color} 30%, ${kpi.color}80 90%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '2.5rem', md: '3rem' },
                            lineHeight: 1,
                            mb: 1
                          }}
                        >
                          {total.toLocaleString()}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Ø {average} pro Tag
                        </Typography>
                      </Box>

                      {/* Before/After Comparison Visualization */}
                      {beforeAfterData && businessCreatedAt && (
                        <Box 
                          sx={{ 
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Stack spacing={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <ShowChart sx={{ color: kpi.color, fontSize: 16 }} />
                              <Typography 
                                variant="body2" 
                                fontWeight="bold"
                                color="text.primary"
                              >
                                Performance-Entwicklung
                              </Typography>
                            </Box>
                            
                            <Grid container spacing={2}>
                              {/* Before Period */}
                              <Grid item xs={6}>
                                <Box 
                                  sx={{ 
                                    textAlign: 'center',
                                    p: 1.5,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 1.5,
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                                  >
                                    Vorher
                                  </Typography>
                                  <Typography 
                                    variant="h6" 
                                    fontWeight="bold"
                                    color="text.primary"
                                    sx={{ lineHeight: 1.2 }}
                                  >
                                    {beforeAfterData.beforeAvgPerDay.toFixed(1)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    / Tag 
                                  </Typography>
                                </Box>
                              </Grid>

                              {/* After Period */}
                              <Grid item xs={6}>
                                <Box 
                                  sx={{ 
                                    textAlign: 'center',
                                    p: 1.5,
                                    bgcolor: `${kpi.color}08`,
                                    borderRadius: 1.5,
                                    border: `1px solid ${kpi.color}20`
                                  }}
                                >
                                  <Typography 
                                    variant="caption"
                                    sx={{ 
                                      color: kpi.color,
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5 
                                    }}
                                  >
                                    Nachher
                                  </Typography>
                                  <Typography 
                                    variant="h6" 
                                    fontWeight="bold"
                                    sx={{ color: kpi.color, lineHeight: 1.2 }}
                                  >
                                    {beforeAfterData.afterAvgPerDay.toFixed(1)}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ color: kpi.color }}
                                  >
                                    / Tag
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>

                            {/* Progress Bar Visualization */}
                            {hasImprovement && (
                              <Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Typography variant="caption" color="text.secondary">
                                    Verbesserung
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    fontWeight="bold"
                                    color={improvementPercentage > 0 ? "success.main" : "error.main"}
                                  >
                                    {improvementPercentage > 0 ? '+' : ''}{improvementPercentage.toFixed(1)}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(Math.abs(improvementPercentage), 100)}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#f0f0f0',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: improvementPercentage > 0 ? '#4caf50' : '#f44336',
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Time Series Chart for Three KPIs */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  📈 KPI-Zeitverlauf ({periodOptions.find(p => p.value === selectedPeriod)?.label})
                </Typography>
                {businessCreatedAt && (
                  <Chip 
                    icon={<CalendarToday />}
                    label={`Unternehmen hinzugefügt: ${new Date(businessCreatedAt).toLocaleDateString('de-DE')}`}
                    variant="outlined" 
                    size="small"
                    sx={{ color: '#e91e63', borderColor: '#e91e63' }}
                  />
                )}
              </Box>
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="formattedDate"
                      tick={{ fontSize: 12 }}
                      interval={chartData.length > 7 ? Math.floor(chartData.length / 7) : 0}
                    />
                    <YAxis />
                    <RechartsTooltip 
                      labelFormatter={(label) => {
                        let tooltip = `Datum: ${label}`;
                        if (label === creationDateForChart) {
                          tooltip += ' (Unternehmen hinzugefügt)';
                        }
                        return tooltip;
                      }}
                      formatter={(value, name) => {
                        const kpi = coreKPIs.find(k => k.key === name);
                        return [value?.toLocaleString() || 0, kpi?.label || name];
                      }}
                    />
                    <Legend />
                    
                    {/* Business Creation Date Reference Line */}
                    {creationDateForChart && (
                      <ReferenceLine 
                        x={creationDateForChart} 
                        stroke="#e91e1eff" 
                        strokeDasharray="4 5" 
                        strokeWidth={4}
                        label={{ 
                          value: "Unternehmen hinzugefügt", 
                          position: "top",
                          style: { 
                            fontSize: 1, 
                            fontWeight: 'bold',
                            fill: '#e91e63'
                          }
                        }}
                      />
                    )}
                    
                    {coreKPIs.map(kpi => (
                      <Line
                        key={kpi.key}
                        type="monotone"
                        dataKey={kpi.key}
                        stroke={kpi.color}
                        strokeWidth={3}
                        dot={{ fill: kpi.color, strokeWidth: 2, r: 5 }}
                        name={kpi.label}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          
        </>
      )}

      {!insightsData && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Insights sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="textSecondary" gutterBottom>
              Keine Insights-Daten verfügbar
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Für diese Business-Location sind keine Performance-Daten verfügbar.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InsightsPage;